import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Trash2, Package, ShoppingBag, IndianRupee, Users, Store, Power, AlertTriangle, Milk, Megaphone, Sparkles, Wallet, ShieldCheck, ArrowDownCircle, ArrowUpCircle, Upload, Download, Copy, CheckCircle2, XCircle, QrCode, FileText } from "lucide-react";
import { toast } from "sonner";
import { useBrands } from "@/context/BrandContext";
import { useAuth } from "@/context/AuthContext";
import { paymentStatusClass } from "@/lib/statusBadge";

const STATUSES = ["pending", "confirmed", "packed", "out_for_delivery", "delivered", "cancelled"];

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-xl border border-[#2D2622]/10 p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs uppercase tracking-wider text-[#8C8581] font-semibold">{label}</p>
        <p className="font-heading font-bold text-2xl text-[#2D2622]">{value}</p>
      </div>
    </div>
  );
}

function ProductDialog({ trigger, initial, brands, onSave }) {
  const [open, setOpen] = useState(false);
  const init = () => initial || { name: "", category: "", description: "", price: 0, unit: "", image_url: "", stock: 100, brand: brands[0]?.key || "teaboy" };
  const [form, setForm] = useState(init);
  useEffect(() => { if (open) setForm(init()); }, [open]); // eslint-disable-line
  const ch = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription>Manage product details, pricing and stock.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!initial && (
            <div>
              <Label>Brand</Label>
              <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
                <SelectTrigger data-testid="pf-brand"><SelectValue /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Name</Label><Input data-testid="pf-name" value={form.name} onChange={ch("name")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Input data-testid="pf-category" value={form.category} onChange={ch("category")} /></div>
            <div><Label>Unit</Label><Input data-testid="pf-unit" value={form.unit} onChange={ch("unit")} placeholder="kg, litre, pack" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Price (INR)</Label><Input data-testid="pf-price" type="number" step="0.01" value={form.price} onChange={ch("price")} /></div>
            <div><Label>Stock</Label><Input data-testid="pf-stock" type="number" value={form.stock} onChange={ch("stock")} /></div>
          </div>
          <div><Label>Image URL</Label><Input data-testid="pf-image" value={form.image_url} onChange={ch("image_url")} /></div>
          <div><Label>Description</Label><Textarea data-testid="pf-desc" value={form.description} onChange={ch("description")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="pf-save" className="bg-[#C85A32] hover:bg-[#A64A28] text-white" onClick={async () => {
            try {
              await onSave({ ...form, price: parseFloat(form.price), stock: parseInt(form.stock || 0) });
              setOpen(false);
            } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FranchiseDialog({ trigger, brands, onSave }) {
  const [open, setOpen] = useState(false);
  const init = () => ({ name: "", store_name: "", email: "", phone: "", password: "", store_code: "", brand: brands[0]?.key || "teaboy" });
  const [form, setForm] = useState(init);
  useEffect(() => { if (open) setForm(init()); }, [open]); // eslint-disable-line
  const ch = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New franchise account</DialogTitle>
          <DialogDescription>Create a new franchise outlet login under a specific brand.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Brand</Label>
            <Select value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}>
              <SelectTrigger data-testid="ff-brand"><SelectValue /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tea outlet / store name</Label><Input data-testid="ff-store-name" value={form.store_name} onChange={ch("store_name")} placeholder="Sharma Tea Stall" /></div>
          <div><Label>Owner name</Label><Input data-testid="ff-name" value={form.name} onChange={ch("name")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input data-testid="ff-email" type="email" value={form.email} onChange={ch("email")} /></div>
            <div><Label>Phone</Label><Input data-testid="ff-phone" value={form.phone} onChange={ch("phone")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Password</Label><Input data-testid="ff-password" type="password" value={form.password} onChange={ch("password")} /></div>
            <div><Label>Custom store code (optional)</Label><Input data-testid="ff-code" value={form.store_code} onChange={ch("store_code")} placeholder="auto" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="ff-save" className="bg-[#C85A32] hover:bg-[#A64A28] text-white" onClick={async () => {
            try {
              if (!form.store_name || !form.name || !form.email || !form.password) return toast.error("All fields except store code are required");
              await onSave(form);
              setOpen(false);
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }}>Create franchise</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementDialog({ trigger, initial, brands, onSave }) {
  const [open, setOpen] = useState(false);
  const init = () => initial || { title: "", body: "", type: "info", brand: "", audience: "all", pinned: false };
  const [form, setForm] = useState(init);
  useEffect(() => { if (open) setForm(init()); }, [open]); // eslint-disable-line
  const ch = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit announcement" : "New announcement"}</DialogTitle>
          <DialogDescription>Broadcast a message to franchise partners.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="af-title" value={form.title} onChange={ch("title")} /></div>
          <div><Label>Body</Label><Textarea data-testid="af-body" rows={4} value={form.body} onChange={ch("body")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="af-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info / News</SelectItem>
                  <SelectItem value="alert">Alert</SelectItem>
                  <SelectItem value="new_product">New Product</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Audience</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger data-testid="af-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All franchises</SelectItem>
                  <SelectItem value="milk_partners">Milk partners only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Brand (blank = both)</Label>
            <Select value={form.brand || "__all__"} onValueChange={(v) => setForm({ ...form, brand: v === "__all__" ? null : v })}>
              <SelectTrigger data-testid="af-brand"><SelectValue placeholder="Both brands" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Both brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.key} value={b.key}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="af-pinned" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />
            Pin as banner on storefront top (always-visible)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="af-save" className="bg-[#C85A32] hover:bg-[#A64A28] text-white" onClick={async () => {
            try {
              if (!form.title || !form.body) return toast.error("Title and body required");
              await onSave({ ...form, brand: form.brand || null });
              setOpen(false);
            } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
          }}>{initial ? "Save" : "Publish"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WalletDialog({ trigger, franchise, onClose }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ balance: 0, transactions: [] });
  const [form, setForm] = useState({ type: "credit", amount: "", reason: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!franchise) return;
    const { data } = await api.get(`/admin/franchises/${franchise.id}/wallet`);
    setData(data);
  };
  useEffect(() => { if (open) { load(); setForm({ type: "credit", amount: "", reason: "" }); } }, [open]); // eslint-disable-line

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    if (!form.reason.trim()) return toast.error("Reason is required");
    setLoading(true);
    try {
      await api.post(`/admin/franchises/${franchise.id}/wallet/adjust`, {
        type: form.type, amount: amt, reason: form.reason.trim(),
      });
      toast.success(`Wallet ${form.type}ed by ₹${amt.toFixed(2)}`);
      setForm({ type: "credit", amount: "", reason: "" });
      await load();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Wallet — {franchise?.store_name} <span className="font-mono text-[#C85A32]">{franchise?.store_code}</span></DialogTitle>
          <DialogDescription>Issue credit (e.g. damaged product refund) or debit adjustments.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-[#2D2622]/10 p-4 bg-[#F5F2EB]">
          <p className="text-xs uppercase tracking-wider text-[#8C8581] font-semibold">Current balance</p>
          <p className="font-heading font-bold text-3xl text-[#2D2622]" data-testid="wallet-balance">₹{Number(data.balance || 0).toFixed(2)}</p>
        </div>

        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-3">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="wf-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Credit (+)</SelectItem>
                <SelectItem value="debit">Debit (−)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3">
            <Label>Amount (₹)</Label>
            <Input data-testid="wf-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div className="col-span-6">
            <Label>Reason</Label>
            <Input data-testid="wf-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Damaged 2 kg sugar — refunded" />
          </div>
        </div>
        <Button data-testid="wf-submit" disabled={loading} onClick={submit} className="bg-[#C85A32] hover:bg-[#A64A28] text-white">
          {form.type === "credit" ? "Add credit" : "Apply debit"}
        </Button>

        <div>
          <p className="text-xs uppercase tracking-wider text-[#8C8581] font-semibold mb-2">Transactions</p>
          <div className="max-h-72 overflow-auto border border-[#2D2622]/10 rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead>
                <TableHead>Reason</TableHead><TableHead>Balance</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.transactions?.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-[#8C8581] py-6">No transactions yet.</TableCell></TableRow>
                )}
                {data.transactions?.map((t) => (
                  <TableRow key={t.id} data-testid={`wallet-txn-${t.id}`}>
                    <TableCell className="text-xs text-[#8C8581]">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell>
                      {t.type === "credit"
                        ? <span className="inline-flex items-center gap-1 text-[#3D6B42] font-semibold text-xs"><ArrowUpCircle className="w-3.5 h-3.5" />Credit</span>
                        : <span className="inline-flex items-center gap-1 text-[#A6291E] font-semibold text-xs"><ArrowDownCircle className="w-3.5 h-3.5" />Debit</span>}
                    </TableCell>
                    <TableCell className="font-semibold">{t.type === "credit" ? "+" : "−"}₹{Number(t.amount).toFixed(2)}</TableCell>
                    <TableCell className="text-xs">{t.reason}{t.ref_order_no && <span className="text-[#8C8581]"> · {t.ref_order_no}</span>}</TableCell>
                    <TableCell className="font-mono text-xs">₹{Number(t.balance_after).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RefundOrderDialog({ trigger, order, onSaved }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("Damaged product refund");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setAmount(""); setReason("Damaged product refund"); } }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a positive amount");
    setLoading(true);
    try {
      await api.post(`/admin/orders/${order.id}/refund-to-wallet`, { amount: amt, reason: reason.trim() });
      toast.success(`Refunded ₹${amt.toFixed(2)} to ${order.store_code} wallet`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refund to wallet — {order?.order_no}</DialogTitle>
          <DialogDescription>Credit a refund (damaged / short delivery) to <span className="font-mono text-[#C85A32]">{order?.store_code}</span>. Partner can apply this credit on future orders.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount (₹)</Label><Input data-testid="rf-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Max ~${(order?.total || 0).toFixed(2)}`} /></div>
          <div><Label>Reason</Label><Textarea data-testid="rf-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="rf-submit" disabled={loading} onClick={submit} className="bg-[#C85A32] hover:bg-[#A64A28] text-white">Issue refund</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffDialog({ trigger, initial, onSave }) {
  const [open, setOpen] = useState(false);
  const init = () => initial || { name: "", email: "", phone: "", password: "" };
  const [form, setForm] = useState(init);
  useEffect(() => { if (open) setForm(init()); }, [open]); // eslint-disable-line
  const ch = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit staff" : "New staff / dispatch login"}</DialogTitle>
          <DialogDescription>Staff can view orders &amp; update dispatch status. They cannot edit products, franchises or wallets.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="sf-name" value={form.name} onChange={ch("name")} /></div>
          {!initial && (
            <div><Label>Email</Label><Input data-testid="sf-email" type="email" value={form.email} onChange={ch("email")} /></div>
          )}
          <div><Label>Phone</Label><Input data-testid="sf-phone" value={form.phone} onChange={ch("phone")} /></div>
          <div><Label>{initial ? "New password (leave blank to keep)" : "Password"}</Label><Input data-testid="sf-password" type="password" value={form.password} onChange={ch("password")} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="sf-save" className="bg-[#C85A32] hover:bg-[#A64A28] text-white" onClick={async () => {
            try {
              if (!initial && (!form.name || !form.email || !form.password)) return toast.error("Name, email, password required");
              await onSave(form);
              setOpen(false);
            } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
          }}>{initial ? "Save" : "Create staff"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportDialog({ kind, trigger, onDone }) {
  // kind: "products" | "franchises"
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const labels = kind === "products"
    ? { title: "Bulk import products", endpoint: "/admin/products/import", tplUrl: "/admin/products/import/template", tplName: "products_template.csv",
        hint: "Columns: name, category, description, price, unit, image_url, stock, brand (teaboy | blackpekoe)." }
    : { title: "Bulk import franchise partners", endpoint: "/admin/franchises/import", tplUrl: "/admin/franchises/import/template", tplName: "franchises_template.csv",
        hint: "Columns: name, email, phone, store_name, brand (teaboy | blackpekoe), is_milk_partner (yes/no). Passwords are auto-generated and shown once below — copy & share with partners." };

  useEffect(() => { if (!open) { setFile(null); setResult(null); } }, [open]);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(labels.tplUrl, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = labels.tplName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(labels.endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.imported} of ${data.total}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const copyAll = () => {
    if (!result?.created) return;
    const lines = result.created.map((c) => `${c.store_code}\t${c.name}\t${c.email}\t${c.password}`);
    const txt = "Store Code\tName\tEmail\tPassword\n" + lines.join("\n");
    navigator.clipboard.writeText(txt);
    toast.success(`Copied ${result.created.length} credentials to clipboard`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{labels.title}</DialogTitle>
          <DialogDescription>{labels.hint}</DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <Button variant="outline" onClick={downloadTemplate} data-testid={`tpl-${kind}`}>
              <Download className="w-4 h-4 mr-1.5" />Download CSV template
            </Button>
            <div className="rounded-lg border-2 border-dashed border-[#2D2622]/20 p-6 text-center bg-[#FAF8F3]">
              <Upload className="w-8 h-8 mx-auto text-[#8C8581] mb-2" />
              <input
                type="file" accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid={`file-${kind}`}
                className="block mx-auto text-sm"
              />
              {file && <p className="text-xs text-[#5C5753] mt-2 font-mono">{file.name} • {Math.round(file.size / 1024)} KB</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid={`submit-${kind}`} disabled={busy || !file} onClick={submit} className="bg-[#C85A32] hover:bg-[#A64A28] text-white">
                {busy ? "Uploading…" : "Upload & import"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg p-3 bg-[#E8F5E9] border border-[#3D6B42]/20">
                <p className="text-xs uppercase tracking-wider text-[#3D6B42] font-semibold">Imported</p>
                <p className="font-heading font-bold text-2xl text-[#3D6B42]" data-testid={`res-imported-${kind}`}>{result.imported}</p>
              </div>
              <div className="rounded-lg p-3 bg-[#FFEBEE] border border-[#A6291E]/20">
                <p className="text-xs uppercase tracking-wider text-[#A6291E] font-semibold">Skipped</p>
                <p className="font-heading font-bold text-2xl text-[#A6291E]" data-testid={`res-skipped-${kind}`}>{result.skipped?.length || 0}</p>
              </div>
              <div className="rounded-lg p-3 bg-[#F5F2EB] border border-[#2D2622]/10">
                <p className="text-xs uppercase tracking-wider text-[#8C8581] font-semibold">Total rows</p>
                <p className="font-heading font-bold text-2xl text-[#2D2622]">{result.total}</p>
              </div>
            </div>

            {kind === "franchises" && result.created?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-[#2D2622]">
                    <CheckCircle2 className="w-4 h-4 inline mr-1 text-[#3D6B42]" />
                    Auto-generated credentials (shown once — copy now)
                  </p>
                  <Button size="sm" variant="outline" onClick={copyAll} data-testid="copy-all-creds">
                    <Copy className="w-3.5 h-3.5 mr-1" />Copy all
                  </Button>
                </div>
                <div className="max-h-64 overflow-auto border border-[#2D2622]/10 rounded-lg">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Store code</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Password</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {result.created.map((c) => (
                        <TableRow key={c.email}>
                          <TableCell className="font-mono font-bold text-[#C85A32]">{c.store_code}</TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell className="text-xs">{c.email}</TableCell>
                          <TableCell className="font-mono text-sm">{c.password}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {result.skipped?.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-[#A6291E] mb-2">
                  <XCircle className="w-4 h-4 inline mr-1" />
                  Skipped rows
                </p>
                <div className="max-h-48 overflow-auto border border-[#A6291E]/20 rounded-lg">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Row</TableHead><TableHead>{kind === "products" ? "Name" : "Email"}</TableHead><TableHead>Error</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {result.skipped.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono">{s.row}</TableCell>
                          <TableCell className="text-xs">{s.name || s.email || "—"}</TableCell>
                          <TableCell className="text-xs text-[#A6291E]">{s.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={() => setOpen(false)} className="bg-[#C85A32] hover:bg-[#A64A28] text-white">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProductQRDialog({ product, trigger }) {
  const [open, setOpen] = useState(false);
  const src = `${API}/products/${product.id}/qr`;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{product.name}</DialogTitle>
          <DialogDescription>Catalog QR — print and stick on product cartons. Scanning opens the product card.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <img src={src} alt={`QR for ${product.name}`} className="w-56 h-56 border border-[#2D2622]/15 rounded-lg bg-white" data-testid={`product-qr-img-${product.id}`} />
          <p className="text-xs font-mono text-[#8C8581]">PRD:{product.id}</p>
          <a href={src} download={`qr_${product.name.replace(/\s+/g, "_")}.png`} className="inline-flex items-center gap-1.5 text-sm text-[#C85A32] font-semibold hover:underline" data-testid={`product-qr-dl-${product.id}`}>
            <Download className="w-4 h-4" /> Download PNG
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

async function downloadDispatchSlip(order) {
  try {
    const res = await api.get(`/orders/${order.id}/dispatch-slip`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `dispatch_${order.order_no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Could not generate dispatch slip");
  }
}

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { brands } = useBrands();
  const [brandFilter, setBrandFilter] = useState("all");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [staff, setStaff] = useState([]);
  const [lowStock, setLowStock] = useState([]);

  const loadAll = async () => {
    const bParam = brandFilter === "all" ? {} : { brand: brandFilter };
    const [s, o, f, ls] = await Promise.all([
      api.get("/admin/stats", { params: bParam }),
      api.get("/admin/orders", { params: bParam }),
      api.get("/admin/franchises", { params: bParam }),
      api.get("/admin/low-stock", { params: { threshold: 20, ...bParam } }),
    ]);
    setStats(s.data); setOrders(o.data); setFranchises(f.data); setLowStock(ls.data);

    // Products: combine both brands when "all", else one brand
    if (brandFilter === "all") {
      const lists = await Promise.all(brands.map((b) => api.get("/products", { params: { brand: b.key } })));
      setProducts(lists.flatMap((r) => r.data));
    } else {
      const { data } = await api.get("/products", { params: { brand: brandFilter } });
      setProducts(data);
    }

    if (isAdmin) {
      try { const r = await api.get("/admin/staff"); setStaff(r.data); } catch { /* staff endpoint admin only */ }
    }
  };

  useEffect(() => { if (brands.length) loadAll(); }, [brandFilter, brands.length]); // eslint-disable-line

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/orders/${id}/status`, { status }); toast.success("Status updated"); loadAll(); }
    catch { toast.error("Update failed"); }
  };

  const saveProduct = async (form, id) => {
    if (id) await api.patch(`/admin/products/${id}`, form);
    else await api.post("/admin/products", form);
    toast.success(id ? "Product updated" : "Product created");
    loadAll();
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Deactivate this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product removed");
    loadAll();
  };

  const saveFranchise = async (form) => { await api.post("/admin/franchises", form); toast.success("Franchise created"); loadAll(); };
  const toggleFranchise = async (uid, active) => {
    await api.patch(`/admin/franchises/${uid}`, { franchise_active: active });
    toast.success(active ? "Franchise activated" : "Franchise deactivated"); loadAll();
  };
  const toggleMilkPartner = async (uid, enabled) => {
    await api.patch(`/admin/franchises/${uid}`, { is_milk_partner: enabled });
    toast.success(enabled ? "Marked as milk partner" : "Milk-partner access removed");
    loadAll();
  };

  const saveStaff = async (form, id) => {
    if (id) await api.patch(`/admin/staff/${id}`, form);
    else await api.post("/admin/staff", form);
    toast.success(id ? "Staff updated" : "Staff created");
    loadAll();
  };
  const toggleStaff = async (uid, active) => {
    await api.patch(`/admin/staff/${uid}`, { is_active: active });
    toast.success(active ? "Staff activated" : "Staff deactivated");
    loadAll();
  };
  const deleteStaff = async (uid) => {
    if (!window.confirm("Delete this staff account?")) return;
    await api.delete(`/admin/staff/${uid}`);
    toast.success("Staff deleted");
    loadAll();
  };

  const [announcements, setAnnouncements] = useState([]);
  const loadAnnouncements = async () => {
    if (!isAdmin) return; // staff role has no announcements access
    try {
      const { data } = await api.get("/admin/announcements");
      setAnnouncements(data);
    } catch { /* admin only */ }
  };
  useEffect(() => { loadAnnouncements(); }, [isAdmin]); // eslint-disable-line
  const saveAnnouncement = async (form, id) => {
    if (id) await api.patch(`/admin/announcements/${id}`, form);
    else await api.post("/admin/announcements", form);
    toast.success(id ? "Announcement updated" : "Announcement published");
    loadAnnouncements();
  };
  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${id}`);
    toast.success("Deleted");
    loadAnnouncements();
  };
  const toggleAnnouncementActive = async (a) => {
    await api.patch(`/admin/announcements/${a.id}`, { is_active: !a.is_active });
    loadAnnouncements();
  };

  const milkProducts = products.filter((p) => p.brand === "teaboy" && p.category === "Milk");
  const milkPartners = franchises.filter((f) => f.is_milk_partner);

  const forceZohoSync = async (id) => {
    toast.info("Syncing to Zoho…");
    const { data } = await api.post(`/admin/orders/${id}/zoho-sync`);
    if (data.invoice?.ok) {
      toast.success(`Synced • Zoho invoice ${data.invoice.invoice_number || data.invoice.invoice_id}`);
    } else {
      toast.error(`Sync failed: ${data.invoice?.reason || "unknown"}`);
    }
    loadAll();
  };

  const brandLabel = (key) => brands.find((b) => b.key === key)?.name || key || "—";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="overline">Admin</p>
          <h1 className="font-heading font-bold text-3xl text-[#2D2622] mt-2">Operations dashboard</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href="/admin/scan" data-testid="admin-scan-link"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white bg-[#C85A32] hover:bg-[#A64A28]">
            <QrCode className="w-4 h-4" /> Scan
          </a>
          <span className="text-xs uppercase tracking-wider text-[#8C8581] font-semibold ml-2">Brand:</span>
          <button data-testid="brand-filter-all" onClick={() => setBrandFilter("all")}
            className="px-3 py-1.5 rounded-full text-sm font-semibold border"
            style={{ background: brandFilter === "all" ? "#2D2622" : "white", color: brandFilter === "all" ? "white" : "#2D2622", borderColor: "rgba(45,38,34,0.15)" }}>
            All brands
          </button>
          {brands.map((b) => (
            <button key={b.key} data-testid={`brand-filter-${b.key}`} onClick={() => setBrandFilter(b.key)}
              className="px-3 py-1.5 rounded-full text-sm font-semibold border inline-flex items-center gap-2"
              style={{ background: brandFilter === b.key ? b.primary : "white", color: brandFilter === b.key ? "white" : "#2D2622", borderColor: brandFilter === b.key ? b.primary : "rgba(45,38,34,0.15)" }}>
              <img src={b.logo_url} alt="" className="h-4 w-auto object-contain rounded-sm px-1 py-0.5" style={{ background: b.logo_bg || "#FFFFFF" }} />
              {b.name}
            </button>
          ))}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total orders" value={stats.total_orders} icon={ShoppingBag} accent="bg-[#C85A32]" />
          <StatCard label="Revenue" value={`₹${stats.revenue.toFixed(0)}`} icon={IndianRupee} accent="bg-[#3D6B42]" />
          <StatCard label="Customers" value={stats.customers} icon={Users} accent="bg-[#2B5A73]" />
          <StatCard label="Active products" value={stats.products} icon={Package} accent="bg-[#E69C24]" />
        </div>
      )}

      {lowStock.length > 0 && (
        <div data-testid="low-stock-alert" className="mb-8 bg-[#FFF5EC] border border-[#E69C24]/30 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E69C24] text-white flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-heading font-semibold text-[#2D2622]">{lowStock.length} product{lowStock.length > 1 ? "s" : ""} running low (≤ 20 in stock)</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {lowStock.slice(0, 8).map((p) => (
                  <span key={p.id} data-testid={`low-stock-item-${p.id}`} className="inline-flex items-center gap-2 bg-white border border-[#E69C24]/30 rounded-full px-3 py-1 text-sm">
                    <span className="font-semibold text-[#2D2622]">{p.name}</span>
                    <span className="text-xs text-[#8C8581]">[{brandLabel(p.brand)}]</span>
                    <span className="text-[#A64A28] font-bold">×{p.stock}</span>
                  </span>
                ))}
                {lowStock.length > 8 && (<span className="text-sm text-[#5C5753] self-center">+{lowStock.length - 8} more</span>)}
              </div>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger data-testid="tab-orders" value="orders">Orders</TabsTrigger>
          {isAdmin && <TabsTrigger data-testid="tab-products" value="products">Products</TabsTrigger>}
          <TabsTrigger data-testid="tab-franchises" value="franchises">Franchises</TabsTrigger>
          {isAdmin && (
            <TabsTrigger data-testid="tab-milk" value="milk" className="data-[state=active]:text-[#C85A32]">
              <Milk className="w-3.5 h-3.5 mr-1.5" /> Milk (Chennai)
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger data-testid="tab-announcements" value="announcements">
              <Megaphone className="w-3.5 h-3.5 mr-1.5" /> Announcements
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger data-testid="tab-staff" value="staff">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Staff
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead><TableHead>Brand</TableHead><TableHead>Franchise</TableHead>
                  <TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Payment / Txn</TableHead><TableHead>Status</TableHead><TableHead>Zoho</TableHead>{isAdmin && <TableHead>Refund</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.length === 0 && (
                  <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-[#8C8581] py-10">No orders.</TableCell></TableRow>
                )}
                {orders.map((o) => (
                  <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                    <TableCell>
                      <p className="font-semibold">{o.order_no}</p>
                      <p className="text-xs text-[#8C8581]">{new Date(o.created_at).toLocaleString()}</p>
                      <button
                        type="button"
                        data-testid={`dispatch-slip-${o.id}`}
                        onClick={() => downloadDispatchSlip(o)}
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#C85A32] hover:underline"
                      >
                        <FileText className="w-3 h-3" /> Dispatch slip
                      </button>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white"
                        style={{ background: brands.find((b) => b.key === o.brand)?.primary || "#8C8581" }}>{brandLabel(o.brand)}</span>
                    </TableCell>
                    <TableCell>
                      <p className="font-semibold text-sm">{o.store_name || "—"}</p>
                      <p className="text-xs text-[#C85A32] font-mono font-bold">{o.store_code || ""}</p>
                      <p className="text-xs text-[#8C8581]">{o.user_email}</p>
                    </TableCell>
                    <TableCell><p className="text-sm">{o.items.length} items</p></TableCell>
                    <TableCell>
                      <p className="font-semibold">₹{o.total.toFixed(2)}</p>
                      {Number(o.wallet_amount_used || 0) > 0 && (
                        <p className="text-[10px] text-[#3D6B42] font-semibold">Wallet ₹{Number(o.wallet_amount_used).toFixed(2)} · Paid ₹{Number(o.amount_payable || 0).toFixed(2)}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge ${paymentStatusClass(o.payment_status)}`}>{o.payment_status}</span>
                      {o.razorpay_payment_id ? (
                        <p className="text-[10px] font-mono text-[#2B5A73] mt-1 break-all" data-testid={`txn-id-${o.id}`} title="Razorpay payment ID">
                          {o.razorpay_payment_id}
                        </p>
                      ) : o.razorpay_order_id ? (
                        <p className="text-[10px] font-mono text-[#8C8581] mt-1 break-all" title="Razorpay order ID (pending capture)">
                          {o.razorpay_order_id}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                        <SelectTrigger data-testid={`status-select-${o.id}`} className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {o.zoho_invoice_number ? (
                        <div className="flex flex-col gap-1" data-testid={`zoho-cell-${o.id}`}>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#3D6B42]">✓ {o.zoho_invoice_number}</span>
                          {o.zoho_payment_id && <span className="text-[9px] text-[#8C8581]">Pay rec.</span>}
                        </div>
                      ) : (
                        <Button data-testid={`zoho-sync-${o.id}`} size="sm" variant="outline" onClick={() => forceZohoSync(o.id)} className="text-[#C85A32]">
                          Sync
                        </Button>
                      )}
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <RefundOrderDialog order={o} onSaved={loadAll}
                          trigger={<Button data-testid={`refund-btn-${o.id}`} size="sm" variant="outline" className="text-[#3D6B42]"><Wallet className="w-3.5 h-3.5 mr-1" />Refund</Button>} />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="mb-4 flex justify-end gap-2">
            <BulkImportDialog kind="products" onDone={loadAll}
              trigger={<Button data-testid="import-products-btn" variant="outline" className="border-[#C85A32] text-[#C85A32]"><Upload className="w-4 h-4 mr-1" />Import CSV</Button>} />
            <ProductDialog brands={brands}
              trigger={<Button data-testid="new-product-btn" className="bg-[#C85A32] hover:bg-[#A64A28] text-white"><Plus className="w-4 h-4 mr-1" />New product</Button>}
              onSave={(f) => saveProduct(f)} />
          </div>
          <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Product</TableHead><TableHead>Brand</TableHead><TableHead>Category</TableHead>
                <TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-product-${p.id}`}>
                    <TableCell className="flex items-center gap-3">
                      <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                      <p className="font-semibold">{p.name}</p>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white"
                        style={{ background: brands.find((b) => b.key === p.brand)?.primary || "#8C8581" }}>{brandLabel(p.brand)}</span>
                    </TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>₹{p.price.toFixed(2)} / {p.unit}</TableCell>
                    <TableCell>{p.stock}</TableCell>
                    <TableCell className="text-right">
                      <ProductQRDialog product={p}
                        trigger={<Button data-testid={`qr-product-${p.id}`} size="sm" variant="outline" className="mr-2"><QrCode className="w-3.5 h-3.5" /></Button>} />
                      <ProductDialog initial={p} brands={brands}
                        trigger={<Button data-testid={`edit-product-${p.id}`} size="sm" variant="outline"><Edit2 className="w-3.5 h-3.5" /></Button>}
                        onSave={(f) => saveProduct(f, p.id)} />
                      <Button data-testid={`delete-product-${p.id}`} size="sm" variant="outline" className="ml-2 text-[#D94532]" onClick={() => deleteProduct(p.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="franchises" className="mt-6">
          {isAdmin && (
            <div className="mb-4 flex justify-end gap-2">
              <BulkImportDialog kind="franchises" onDone={loadAll}
                trigger={<Button data-testid="import-franchises-btn" variant="outline" className="border-[#C85A32] text-[#C85A32]"><Upload className="w-4 h-4 mr-1" />Import CSV</Button>} />
              <FranchiseDialog brands={brands}
                trigger={<Button data-testid="new-franchise-btn" className="bg-[#C85A32] hover:bg-[#A64A28] text-white"><Plus className="w-4 h-4 mr-1" />New franchise</Button>}
                onSave={saveFranchise} />
            </div>
          )}
          <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Store code</TableHead><TableHead>Brand</TableHead><TableHead>Outlet</TableHead><TableHead>Owner</TableHead>
                <TableHead>Contact</TableHead><TableHead>Wallet</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {franchises.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-[#8C8581] py-10">No franchises.</TableCell></TableRow>
                )}
                {franchises.map((f) => (
                  <TableRow key={f.id} data-testid={`admin-franchise-${f.id}`}>
                    <TableCell><span className="font-mono font-bold text-[#C85A32]">{f.store_code || "—"}</span></TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white"
                        style={{ background: brands.find((b) => b.key === f.brand)?.primary || "#8C8581" }}>{brandLabel(f.brand)}</span>
                    </TableCell>
                    <TableCell><p className="font-semibold flex items-center gap-1.5"><Store className="w-3.5 h-3.5 text-[#8C8581]" />{f.store_name || "—"}</p></TableCell>
                    <TableCell>{f.name}</TableCell>
                    <TableCell><p className="text-sm">{f.email}</p><p className="text-xs text-[#8C8581]">{f.phone || "—"}</p></TableCell>
                    <TableCell>
                      <span data-testid={`wallet-balance-${f.id}`} className={`font-semibold ${Number(f.wallet_balance || 0) > 0 ? "text-[#3D6B42]" : "text-[#8C8581]"}`}>
                        ₹{Number(f.wallet_balance || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell><span className={`status-badge ${f.franchise_active ? "status-paid" : "status-failed"}`}>{f.franchise_active ? "active" : "deactivated"}</span>
                      {f.is_milk_partner && (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[#FFE9D6] text-[#A64A28]" data-testid={`milk-badge-${f.id}`}>
                          <Milk className="w-3 h-3" />Milk · {f.milk_partner_code || ""}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                        <WalletDialog franchise={f} onClose={loadAll}
                          trigger={<Button data-testid={`wallet-btn-${f.id}`} size="sm" variant="outline" className="text-[#3D6B42] mr-2"><Wallet className="w-3.5 h-3.5 mr-1" />Wallet</Button>} />
                      )}
                      {isAdmin && (
                        <Button data-testid={`toggle-franchise-${f.id}`} size="sm" variant="outline" onClick={() => toggleFranchise(f.id, !f.franchise_active)}>
                          <Power className="w-3.5 h-3.5 mr-1" />{f.franchise_active ? "Deactivate" : "Activate"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="milk" className="mt-6">
          <div className="bg-[#FFF5EC] border border-[#E69C24]/30 rounded-xl p-5 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#C85A32] text-white flex items-center justify-center"><Milk className="w-5 h-5" /></div>
            <div>
              <p className="font-heading font-semibold text-[#2D2622]">Milk &amp; dairy — Chennai exclusive</p>
              <p className="text-sm text-[#5C5753]">TEABOY franchises with a Chennai delivery address are auto-promoted to milk partners on their first order. You can also toggle manually below.</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#2D2622]/10 flex items-center justify-between">
                <p className="font-heading font-semibold text-[#2D2622]">Milk products ({milkProducts.length})</p>
                <ProductDialog brands={brands}
                  trigger={<Button data-testid="new-milk-product-btn" size="sm" className="bg-[#C85A32] hover:bg-[#A64A28] text-white"><Plus className="w-4 h-4 mr-1" />Add</Button>}
                  onSave={(f) => saveProduct({ ...f, category: "Milk", brand: "teaboy" })} />
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead className="text-right">Edit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {milkProducts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#8C8581] py-8">No milk products yet.</TableCell></TableRow>}
                  {milkProducts.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-milk-product-${p.id}`}>
                      <TableCell className="flex items-center gap-2">
                        <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                        <span className="font-semibold text-sm">{p.name}</span>
                      </TableCell>
                      <TableCell className="text-sm">₹{p.price.toFixed(2)} / {p.unit}</TableCell>
                      <TableCell className="text-sm">{p.stock}</TableCell>
                      <TableCell className="text-right">
                        <ProductDialog initial={p} brands={brands}
                          trigger={<Button data-testid={`edit-milk-product-${p.id}`} size="sm" variant="outline"><Edit2 className="w-3.5 h-3.5" /></Button>}
                          onSave={(f) => saveProduct(f, p.id)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-[#2D2622]/10">
                <p className="font-heading font-semibold text-[#2D2622]">Milk partners ({milkPartners.length})</p>
                <p className="text-xs text-[#8C8581] mt-0.5">Eligible Chennai TEABOY franchises with a milk-partner code.</p>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>Outlet</TableHead><TableHead>Store</TableHead><TableHead>Milk code</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {milkPartners.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-[#8C8581] py-8">No milk partners yet.</TableCell></TableRow>}
                  {milkPartners.map((f) => (
                    <TableRow key={f.id} data-testid={`milk-partner-row-${f.id}`}>
                      <TableCell><p className="font-semibold text-sm">{f.store_name}</p><p className="text-xs text-[#8C8581]">{f.email}</p></TableCell>
                      <TableCell><span className="font-mono text-xs text-[#5C5753]">{f.store_code}</span></TableCell>
                      <TableCell><span className="font-mono font-bold text-[#C85A32] text-sm">{f.milk_partner_code || "—"}</span></TableCell>
                      <TableCell className="text-right">
                        <Button data-testid={`milk-revoke-${f.id}`} size="sm" variant="outline" className="text-[#D94532]" onClick={() => toggleMilkPartner(f.id, false)}>Revoke</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t border-[#2D2622]/10 p-4">
                <p className="text-xs uppercase tracking-wider text-[#8C8581] font-semibold mb-2">Promote a TEABOY franchise</p>
                <div className="max-h-48 overflow-auto space-y-1">
                  {franchises.filter((f) => f.brand === "teaboy" && !f.is_milk_partner).map((f) => (
                    <div key={f.id} className="flex justify-between items-center text-sm py-1.5 px-2 hover:bg-[#F5F2EB] rounded">
                      <div><span className="font-semibold">{f.store_name}</span><span className="text-xs text-[#8C8581] ml-2">{f.store_code}</span></div>
                      <Button data-testid={`milk-promote-${f.id}`} size="sm" variant="outline" onClick={() => toggleMilkPartner(f.id, true)}>
                        <Milk className="w-3.5 h-3.5 mr-1" />Add as milk partner
                      </Button>
                    </div>
                  ))}
                  {franchises.filter((f) => f.brand === "teaboy" && !f.is_milk_partner).length === 0 && (
                    <p className="text-xs text-[#8C8581] text-center py-4">All TEABOY franchises are already milk partners.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="announcements" className="mt-6">
          <div className="mb-4 flex justify-between items-center">
            <p className="text-sm text-[#5C5753]">Send news, alerts, and product launches to your franchise partners. Pinned posts appear as a banner at the top of the storefront.</p>
            <AnnouncementDialog brands={brands}
              trigger={<Button data-testid="new-announcement-btn" className="bg-[#C85A32] hover:bg-[#A64A28] text-white"><Plus className="w-4 h-4 mr-1" />New announcement</Button>}
              onSave={(f) => saveAnnouncement(f)} />
          </div>
          <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Brand</TableHead>
                <TableHead>Audience</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {announcements.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-[#8C8581] py-10">No announcements yet.</TableCell></TableRow>
                )}
                {announcements.map((a) => (
                  <TableRow key={a.id} data-testid={`admin-ann-${a.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {a.pinned && <span className="text-[9px] uppercase tracking-wider text-[#C85A32] font-bold">Pinned</span>}
                        <p className="font-semibold text-sm">{a.title}</p>
                      </div>
                      <p className="text-xs text-[#8C8581] mt-1 line-clamp-1">{a.body}</p>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                        style={{ background: a.type === "alert" ? "#FBE2DE" : a.type === "new_product" ? "#DCF0DE" : "#E3EEF5",
                                 color: a.type === "alert" ? "#A6291E" : a.type === "new_product" ? "#3D6B42" : "#2B5A73" }}>
                        {a.type === "new_product" ? <span><Sparkles className="w-3 h-3 inline mr-0.5" />New product</span> : a.type}
                      </span>
                    </TableCell>
                    <TableCell><span className="text-sm">{a.brand ? brandLabel(a.brand) : "Both"}</span></TableCell>
                    <TableCell><span className="text-sm">{a.audience === "milk_partners" ? "Milk partners" : "All"}</span></TableCell>
                    <TableCell><span className={`status-badge ${a.is_active ? "status-paid" : "status-failed"}`}>{a.is_active ? "active" : "hidden"}</span></TableCell>
                    <TableCell className="text-right">
                      <AnnouncementDialog initial={a} brands={brands}
                        trigger={<Button data-testid={`edit-ann-${a.id}`} size="sm" variant="outline"><Edit2 className="w-3.5 h-3.5" /></Button>}
                        onSave={(f) => saveAnnouncement(f, a.id)} />
                      <Button data-testid={`toggle-ann-${a.id}`} size="sm" variant="outline" className="ml-2" onClick={() => toggleAnnouncementActive(a)}>
                        <Power className="w-3.5 h-3.5" />
                      </Button>
                      <Button data-testid={`delete-ann-${a.id}`} size="sm" variant="outline" className="ml-2 text-[#D94532]" onClick={() => deleteAnnouncement(a.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="staff" className="mt-6">
            <div className="mb-4 flex justify-between items-center">
              <p className="text-sm text-[#5C5753]">Staff / dispatch logins can view orders and update delivery status. They cannot edit products, franchises or issue wallet credits.</p>
              <StaffDialog onSave={(f) => saveStaff(f)}
                trigger={<Button data-testid="new-staff-btn" className="bg-[#C85A32] hover:bg-[#A64A28] text-white"><Plus className="w-4 h-4 mr-1" />New staff</Button>} />
            </div>
            <div className="bg-white border border-[#2D2622]/10 rounded-xl overflow-hidden">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {staff.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-[#8C8581] py-10">No staff accounts yet.</TableCell></TableRow>
                  )}
                  {staff.map((s) => (
                    <TableRow key={s.id} data-testid={`admin-staff-${s.id}`}>
                      <TableCell><p className="font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#3D6B42]" />{s.name}</p></TableCell>
                      <TableCell><p className="text-sm">{s.email}</p></TableCell>
                      <TableCell><p className="text-sm text-[#8C8581]">{s.phone || "—"}</p></TableCell>
                      <TableCell><span className={`status-badge ${s.franchise_active ? "status-paid" : "status-failed"}`}>{s.franchise_active ? "active" : "deactivated"}</span></TableCell>
                      <TableCell className="text-right">
                        <StaffDialog initial={s} onSave={(f) => saveStaff(f, s.id)}
                          trigger={<Button data-testid={`edit-staff-${s.id}`} size="sm" variant="outline"><Edit2 className="w-3.5 h-3.5" /></Button>} />
                        <Button data-testid={`toggle-staff-${s.id}`} size="sm" variant="outline" className="ml-2" onClick={() => toggleStaff(s.id, !s.franchise_active)}>
                          <Power className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`delete-staff-${s.id}`} size="sm" variant="outline" className="ml-2 text-[#D94532]" onClick={() => deleteStaff(s.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
